import React from "react";
import { FileText, WarningCircle } from "@phosphor-icons/react";

/**
 * CvFreshnessBadge — subtle CV recency indicator for candidate lists.
 *
 * Reads `candidate.resume_updated_at`. Renders:
 *   - green:  ≤ 30 days   ("Fresh")
 *   - cobalt: 31–90 days  ("N mo")
 *   - amber:  91–180 days ("Stale — nudge")
 *   - red:    > 180 days OR no CV ("No CV" | "Old — X mo")
 *
 * Compact — designed for the tag-strip below a candidate's name/title
 * without introducing new columns.
 */

function daysBetween(iso) {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor((Date.now() - t) / (24 * 3600 * 1000));
}

function shortLabel(days) {
    if (days < 1) return "today";
    if (days === 1) return "1d";
    if (days < 30) return `${days}d`;
    if (days < 60) return "1 mo";
    const mo = Math.round(days / 30);
    if (mo < 12) return `${mo} mo`;
    const yr = Math.floor(mo / 12);
    return yr === 1 ? "1 yr" : `${yr} yrs`;
}

export default function CvFreshnessBadge({ candidate, className = "" }) {
    // Feb 17, 2026 — recognise 3 signals for "has CV":
    // 1. Modern R2 uploads: `resume_r2_key` / `resume_url`
    // 2. Legacy candidates (pre-R2, mid-2025): only `resume_text` on the row
    //    — the file itself is gone but the text was extracted at upload time.
    // 3. Otherwise "NO CV" chip.
    const hasR2 = !!(candidate?.resume_r2_key || candidate?.resume_url);
    const hasLegacyText = !hasR2 && !!(candidate?.resume_text && String(candidate.resume_text).trim().length > 0);
    const hasCv = hasR2 || hasLegacyText;
    const updated = candidate?.resume_updated_at || null;
    const days = daysBetween(updated);

    // No CV on file — or CV present but no updated timestamp (legacy)
    if (!hasCv) {
        return (
            <span
                data-testid={`cv-fresh-${candidate?.id}`}
                title="No CV on file — ask the candidate to upload"
                className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 ${className}`}
            >
                <WarningCircle size={9} weight="fill" />
                No CV
            </span>
        );
    }
    if (days === null) {
        // CV present but no timestamp (legacy pre-V1.5.1 uploads OR legacy-text-only candidates)
        return (
            <span
                data-testid={`cv-fresh-${candidate?.id}`}
                title={hasLegacyText && !hasR2 ? "CV text on file (original file not stored)" : "CV on file (upload date unknown)"}
                className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5 ${className}`}
            >
                <FileText size={9} weight="fill" />
                CV on file
            </span>
        );
    }

    let tone;
    let label;
    if (days <= 30) {
        tone = "text-emerald-700 bg-emerald-50 border-emerald-100";
        label = `CV ${shortLabel(days)}`;
    } else if (days <= 90) {
        tone = "text-cobalt bg-cobalt/5 border-cobalt/20";
        label = `CV ${shortLabel(days)}`;
    } else if (days <= 180) {
        tone = "text-amber-700 bg-amber-50 border-amber-100";
        label = `CV ${shortLabel(days)} — nudge`;
    } else {
        tone = "text-red-600 bg-red-50 border-red-100";
        label = `CV ${shortLabel(days)} old`;
    }

    return (
        <span
            data-testid={`cv-fresh-${candidate?.id}`}
            title={`CV last updated ${shortLabel(days)} ago (${new Date(updated).toLocaleDateString()})`}
            className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider border px-1.5 py-0.5 ${tone} ${className}`}
        >
            <FileText size={9} weight="fill" />
            {label}
        </span>
    );
}
